import React, { HTMLAttributes, useEffect, useRef, useState } from 'react';
import './Input.less';
import classNames from 'classnames';
import { useDebounce } from '../../../helper/Hooks';
import { EnvagoAria } from '../../../aria.types';

export interface InputType {
    className?: string;
    placeholder?: string;
    value?: string;
    autoFocus?: boolean;
    onFocusChange?: (hasFocus: boolean) => void;
    maxLength?: number;
    name?: string;
    type?:
        | 'button'
        | 'checkbox'
        | 'color'
        | 'date'
        | 'datetime-local'
        | 'email'
        | 'file'
        | 'hidden'
        | 'image'
        | 'month'
        | 'number'
        | 'password'
        | 'radio'
        | 'range'
        | 'reset'
        | 'search'
        | 'submit'
        | 'tel'
        | 'text'
        | 'time'
        | 'url'
        | 'week';
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    aria?: EnvagoAria;
    autoComplete?: string;
    inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
}

const Input = ({
    className,
    placeholder,
    value,
    onChange,
    type,
    autoFocus = false,
    onKeyDown,
    onFocusChange,
    maxLength,
    name,
    aria,
    autoComplete,
    inputMode,
}: InputType & {
    onChange?: (value: string, event: React.ChangeEvent<HTMLInputElement>) => void;
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [focused, setFocused] = useState(false);
    const debouncedFocus = useDebounce(focused, 100);

    useEffect(() => {
        if (autoFocus && inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    useEffect(() => {
        if (onFocusChange) onFocusChange(debouncedFocus);
    }, [debouncedFocus]);

    return (
        <input
            name={name}
            onFocus={() => {
                setFocused(true);
            }}
            onBlur={() => {
                setFocused(false);
            }}
            autoComplete={autoComplete}
            type={type}
            ref={inputRef}
            maxLength={maxLength}
            onChange={(e) => (onChange ? onChange(e.currentTarget.value, e) : undefined)}
            value={value}
            placeholder={placeholder}
            // Default height only if the caller sets none (two h-* classes would depend on the CSS order)
            className={classNames('form-input rounded-md border w-full text-black placeholder-gray-400 p-2 mt-2 mb-2', { 'h-8': !/(^|\s)h-\S+/.test(className ?? '') }, className)}
            onKeyDown={onKeyDown}
            inputMode={inputMode}
            {...aria}
        />
    );
};

export default Input;
